import React, { useRef } from 'react';
import get from 'lodash.get';
import set from 'lodash.set';
import { ComponentType } from '@philips-internal/oswald-ui-core';
import { ChangeEvent, CollapseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { useSharedRef } from '@philips-internal/oswald-ui-engine';
import { classNames, deprecationWarningMessage, getLostProps, isContentfulString, isDefined, isFunction, isObject, propagateData, trigger } from '@philips-internal/oswald-ui-utils';
import { FieldVisibility } from '../field/field-constants.js';
import { SubSection } from '../heading/subsection/subsection.js';
import { FieldsetProperties } from './fieldset-types.js';
import './fieldset.css';
const FieldsetContainer = {
    acceptedTypes: [
        ComponentType.FORM,
    ],
    name: '',
};
function getValue(data, entry = FieldsetProperties.entry.defaultValue) {
    return get(data, entry) ?? data;
}
function isVisible(options) {
    if (!options.visible) {
        return false;
    }
    return (options.visibility === FieldVisibility.BOTH
        || options.visibility === FieldVisibility.VIEWING && !options.editing
        || options.visibility === FieldVisibility.EDITING && options.editing);
}
function Fieldset(props) {
    const { collapsible = FieldsetProperties.collapsible.defaultValue, data = FieldsetProperties.data.defaultValue, editing = FieldsetProperties.editing.defaultValue, entry = FieldsetProperties.entry.defaultValue, section = FieldsetProperties.section.defaultValue, title, uuid = FieldsetProperties.uuid.defaultValue, visibility = FieldsetProperties.visibility.defaultValue, visible = FieldsetProperties.visible.defaultValue, } = props;
    if (isContentfulString(title)) {
        deprecationWarningMessage({
            deadlineVersion: '7.0.0',
            property: true,
            replaced: 'title',
            scope: 'Fieldset',
            startingVersion: '6.5.0',
        });
    }
    if (isDefined(collapsible) && collapsible !== FieldsetProperties.collapsible.defaultValue) {
        deprecationWarningMessage({
            deadlineVersion: '7.0.0',
            property: true,
            replaced: 'collapsible',
            scope: 'Fieldset',
            startingVersion: '6.5.0',
        });
    }
    const sectionRef = useRef(null);
    useSharedRef({
        name: 'section',
        props,
        ref: sectionRef,
    });
    const visibilityOptions = {
        editing,
        visibility,
        visible,
    };
    if (!isVisible(visibilityOptions)) {
        return null;
    }
    const onCollapse = (collapsed) => {
        if (!isFunction(props.onCollapse)) {
            return;
        }
        props.onCollapse(new CollapseEvent({ collapsed }));
    };
    const onChange = (event) => {
        if (!isFunction(props.onChange)) {
            return;
        }
        if (!isContentfulString(entry)) {
            return props.onChange(event);
        }
        const value = getValue(data, entry);
        const oldValue = isObject(value) ? { ...value } : { [entry]: value };
        props.onChange(new ChangeEvent({
            ...event,
            entry,
            oldValue,
            value: set(oldValue, event.entry ?? '', event.value),
        }));
    };
    const className = classNames({
        'c-fieldset': true,
        [`${props.className}`]: Boolean(props.className),
    });
    const lostProps = getLostProps(FieldsetProperties, props);
    const value = getValue(data, entry) ?? {};
    const sectionProps = {
        showCollapseButton: isDefined(section.showCollapseButton)
            ? section.showCollapseButton
            : collapsible,
        title: isContentfulString(section.title)
            ? section.title
            : title ?? '',
    };
    return (React.createElement("div", { className: className },
        React.createElement(SubSection, { ...sectionProps, container: FieldsetContainer, hideActions: true, onClose: trigger(onCollapse, false), onOpen: trigger(onCollapse, true), ref: sectionRef, uuid: uuid },
            React.createElement("div", { className: "c-fieldset__content o-form" }, propagateData(props.children, value, {
                ...lostProps,
                className: props.className,
                editing: editing,
                onChange,
            })))));
}
export { Fieldset, FieldsetContainer, };
//# sourceMappingURL=fieldset.js.map